/**
 * API Service für Mengenermittlung
 * Verwaltet Kommunikation mit Backend und localStorage Fallback
 */

const API_BASE = '/api';

// Prüft ob API erreichbar ist
let apiAvailable = null;

export async function checkApiAvailable() {
  if (apiAvailable !== null) return apiAvailable;

  try {
    const response = await fetch(`${API_BASE}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    apiAvailable = response.ok;
  } catch (e) {
    apiAvailable = false;
  }

  console.log(`API ${apiAvailable ? 'verfügbar' : 'nicht verfügbar, nutze localStorage'}`);
  return apiAvailable;
}

// === Sync Endpoint (Haupt-Methoden) ===

export async function loadAllData() {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/sync`);
      if (response.ok) {
        const data = await response.json();
        // Auch in localStorage cachen für Offline-Nutzung
        localStorage.setItem('mengenermittlung_data', JSON.stringify({
          materials: data.materials,
          componentTypes: data.componentTypes,
          project: Object.values(data.projects)[0] || null
        }));
        return {
          materials: data.materials || {},
          componentTypes: data.componentTypes || {},
          project: Object.values(data.projects)[0] || null,
          source: 'api'
        };
      }
    } catch (e) {
      console.error('API Sync fehlgeschlagen:', e);
    }
  }

  // Fallback: localStorage
  try {
    const saved = localStorage.getItem('mengenermittlung_data');
    if (saved) {
      const data = JSON.parse(saved);
      return {
        materials: data.materials || {},
        componentTypes: data.componentTypes || {},
        project: data.project || null,
        source: 'localStorage'
      };
    }
  } catch (e) {
    console.error('localStorage laden fehlgeschlagen:', e);
  }

  return { materials: {}, componentTypes: {}, project: null, source: 'default' };
}

export async function saveAllData(materials, componentTypes, project) {
  // Immer in localStorage speichern (Backup)
  localStorage.setItem('mengenermittlung_data', JSON.stringify({ materials, componentTypes, project }));

  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ materials, componentTypes, project })
      });
      return response.ok;
    } catch (e) {
      console.error('API Sync speichern fehlgeschlagen:', e);
    }
  }

  return false;
}

// === Material API ===

export async function saveMaterial(material) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(material)
      });
      return response.ok;
    } catch (e) {
      console.error('Material speichern fehlgeschlagen:', e);
    }
  }

  return false;
}

export async function deleteMaterialApi(materialId) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/materials/${materialId}`, {
        method: 'DELETE'
      });
      return response.ok;
    } catch (e) {
      console.error('Material löschen fehlgeschlagen:', e);
    }
  }

  return false;
}

// === Component Type API ===

export async function saveComponentType(componentType) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/component-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(componentType)
      });
      return response.ok;
    } catch (e) {
      console.error('Bauteiltyp speichern fehlgeschlagen:', e);
    }
  }

  return false;
}

export async function deleteComponentTypeApi(typeId) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/component-types/${typeId}`, {
        method: 'DELETE'
      });
      return response.ok;
    } catch (e) {
      console.error('Bauteiltyp löschen fehlgeschlagen:', e);
    }
  }

  return false;
}

// === Project API ===

export async function loadProjects() {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/projects`);
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.error('Projekte laden fehlgeschlagen:', e);
    }
  }

  // Fallback: localStorage
  try {
    const saved = localStorage.getItem('mengenermittlung_data');
    if (saved) {
      const data = JSON.parse(saved);
      return data.project ? { [data.project.id]: data.project } : {};
    }
  } catch (e) {
    console.error('localStorage Projekte laden fehlgeschlagen:', e);
  }

  return {};
}

export async function loadProject(projectId) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/projects/${projectId}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.error('Projekt laden fehlgeschlagen:', e);
    }
  }

  return null;
}

export async function saveProject(project) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      return response.ok;
    } catch (e) {
      console.error('Projekt speichern fehlgeschlagen:', e);
    }
  }

  return false;
}

export async function deleteProjectApi(projectId) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/projects/${projectId}`, {
        method: 'DELETE'
      });
      return response.ok;
    } catch (e) {
      console.error('Projekt löschen fehlgeschlagen:', e);
    }
  }

  return false;
}

// === PDF Upload ===

export async function uploadPdf(file) {
  const isOnline = await checkApiAvailable();

  if (!isOnline) {
    return { success: false, message: 'API nicht verfügbar' };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/upload-pdf`, {
      method: 'POST',
      body: formData
    });

    return await response.json();
  } catch (e) {
    console.error('PDF Upload fehlgeschlagen:', e);
    return { success: false, message: e.message };
  }
}

// === Export/Import ===

export async function exportData() {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/export`);
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.error('Export fehlgeschlagen:', e);
    }
  }

  // Fallback: localStorage
  const saved = localStorage.getItem('mengenermittlung_data');
  return saved ? JSON.parse(saved) : null;
}

export async function importData(data) {
  const isOnline = await checkApiAvailable();

  if (isOnline) {
    try {
      const response = await fetch(`${API_BASE}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.ok;
    } catch (e) {
      console.error('Import fehlgeschlagen:', e);
    }
  }

  // Fallback: localStorage
  localStorage.setItem('mengenermittlung_data', JSON.stringify(data));
  return true;
}

// === Underlay API (Grundriss-Unterlage) ===

export async function uploadUnderlay(file, page = 0) {
  const isOnline = await checkApiAvailable();

  if (!isOnline) {
    return { success: false, message: 'API nicht verfügbar' };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/upload-underlay?page=${page}`, {
      method: 'POST',
      body: formData
    });

    return await response.json();
  } catch (e) {
    console.error('Underlay Upload fehlgeschlagen:', e);
    return { success: false, message: e.message };
  }
}

export async function deleteUnderlay(underlayId) {
  const isOnline = await checkApiAvailable();

  if (!isOnline) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/underlay/${underlayId}`, {
      method: 'DELETE'
    });
    return response.ok;
  } catch (e) {
    console.error('Underlay löschen fehlgeschlagen:', e);
    return false;
  }
}

// === Geometry API (rhino3dm Backend) ===

/**
 * Prüft ob die Geometrie-Engine verfügbar ist
 */
export async function checkGeometryAvailable() {
  try {
    const response = await fetch(`${API_BASE}/geometry/status`);
    if (response.ok) {
      const data = await response.json();
      return data.rhino3dm_available === true;
    }
  } catch (e) {
    console.warn('Geometry API nicht erreichbar:', e);
  }
  return false;
}

/**
 * Erstellt eine Kurve aus gezeichneten Punkten
 * @param {Array} points - [{x, y}, {x, y}, ...]
 * @param {string} curveType - 'line', 'polyline', 'arc_3pt'
 * @param {boolean} closed - Geschlossene Kurve?
 */
export async function createCurve(points, curveType = 'line', closed = false) {
  try {
    const response = await fetch(`${API_BASE}/geometry/curve/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ points, curve_type: curveType, closed })
    });
    return await response.json();
  } catch (e) {
    console.error('Kurve erstellen fehlgeschlagen:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Hauptworkflow: Punkte → Kurve → Wand → Three.js Mesh
 * @param {Array} points - [{x, y}, {x, y}, ...]
 * @param {Object} options - { curveType, height, thickness, zBase, layerSet, openings }
 */
export async function curveToWall(points, options = {}) {
  const {
    curveType = 'line',
    height = 2750,
    thickness = 185,
    zBase = 0,
    layerSet = null,
    openings = []
  } = options;

  try {
    const response = await fetch(`${API_BASE}/geometry/curve-to-wall`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        points,
        curve_type: curveType,
        height,
        thickness,
        z_base: zBase,
        layer_set: layerSet,
        openings
      })
    });
    return await response.json();
  } catch (e) {
    console.error('Wand-Geometrie erstellen fehlgeschlagen:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Erstellt Box-Geometrie (z.B. für Schächte)
 * @param {Object} origin - {x, y, z}
 * @param {number} width
 * @param {number} depth
 * @param {number} height
 * @param {number} rotationZ - Rotation in Grad
 */
export async function createBoxGeometry(origin, width, depth, height, rotationZ = 0) {
  try {
    const response = await fetch(`${API_BASE}/geometry/box/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origin,
        width,
        depth,
        height,
        rotation_z: rotationZ
      })
    });
    return await response.json();
  } catch (e) {
    console.error('Box-Geometrie erstellen fehlgeschlagen:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Erstellt Wand-Geometrie direkt aus Start/End-Punkten
 * @param {Object} start - {x, y}
 * @param {Object} end - {x, y}
 * @param {Object} options - { height, thickness, zBase, layerSet, openings }
 */
export async function createWallGeometry(start, end, options = {}) {
  const {
    height = 2750,
    thickness = 185,
    zBase = 0,
    layerSet = null,
    openings = []
  } = options;

  try {
    const response = await fetch(`${API_BASE}/geometry/wall/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start,
        end,
        height,
        thickness,
        z_base: zBase,
        layer_set: layerSet,
        openings
      })
    });
    return await response.json();
  } catch (e) {
    console.error('Wand-Geometrie erstellen fehlgeschlagen:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Exportiert Geschoss als .3dm Datei
 * @param {Object} storeyData - { points, walls, spaces, storeyHeight }
 */
export async function export3dm(storeyData) {
  try {
    const response = await fetch(`${API_BASE}/export/3dm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(storeyData)
    });

    if (response.ok) {
      const blob = await response.blob();
      // Download triggern
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'grundriss.3dm';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      return { success: true };
    }

    return { success: false, error: 'Export fehlgeschlagen' };
  } catch (e) {
    console.error('3DM Export fehlgeschlagen:', e);
    return { success: false, error: e.message };
  }
}
